import { setup } from 'xstate';

import { CounterMachineActions } from './counter-machine.actions';
import {
  createDecrementCounterActor,
  createGetCounterValueActor,
  createIncrementCounterActor,
  createResetCounterActor,
} from './counter-machine.services';
import { type CounterMachineContext, type CounterMachineEvents } from './counter-machine.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A factory function to create a state machine for managing a counter using async storage.
 *
 * NOTE: Async storage methods are promises and to handle their async behavior, we use
 *       actors, as well as `waiting` and `ready` states.
 *
 * @param {String} asyncStorageKey A unique key to store counter value in async storage
 */
export function createCounterMachine(asyncStorageKey: string) {
  return setup({
    types: {
      context: {} as CounterMachineContext,
      events: {} as CounterMachineEvents,
    },
    actors: {
      getCounterValue: createGetCounterValueActor(asyncStorageKey),
      incrementCounter: createIncrementCounterActor(asyncStorageKey),
      decrementCounter: createDecrementCounterActor(asyncStorageKey),
      resetCounter: createResetCounterActor(asyncStorageKey),
    },
    actions: CounterMachineActions,
  }).createMachine({
    //
    // ─── Context ─────────────────────────────────────────────────

    context: {
      value: 0,
    },

    // ─── States ──────────────────────────────────────────────────

    initial: 'waiting',
    states: {
      waiting: {
        invoke: {
          id: 'getCounterValue',
          src: 'getCounterValue',
          onError: {},
          onDone: {
            target: 'ready',
            actions: [
              {
                type: 'storeInitialCounterValue',
                params: ({ event }) => ({ storedValue: event.output }),
              },
            ],
          },
        },
      },
      ready: {
        initial: 'idle',
        states: {
          idle: {
            id: 'ready-idle',
            on: {
              INCREMENT: {
                target: '#increment-counter',
              },
              DECREMENT: {
                target: '#decrement-counter',
              },
              RESET: {
                target: '#reset-counter',
              },
            },
          },
          loading: {
            initial: 'idle',
            states: {
              idle: {},
              incrementCounter: {
                id: 'increment-counter',
                invoke: {
                  id: 'incrementCounter',
                  src: 'incrementCounter',
                  onError: {},
                  input: ({ context }) => context,
                  onDone: {
                    target: '#ready-idle',
                    actions: [
                      {
                        type: 'storeCounterValue',
                        params: ({ event }) => ({ value: event.output }),
                      },
                    ],
                  },
                },
              },
              decrementCounter: {
                id: 'decrement-counter',
                invoke: {
                  id: 'decrementCounter',
                  src: 'decrementCounter',
                  onError: {},
                  input: ({ context }) => context,
                  onDone: {
                    target: '#ready-idle',
                    actions: [
                      {
                        type: 'storeCounterValue',
                        params: ({ event }) => ({ value: event.output }),
                      },
                    ],
                  },
                },
              },
              resetCounter: {
                id: 'reset-counter',
                invoke: {
                  id: 'resetCounter',
                  src: 'resetCounter',
                  onError: {},
                  onDone: {
                    target: '#ready-idle',
                    actions: [
                      {
                        type: 'storeCounterValue',
                        params: ({ event }) => ({ value: event.output }),
                      },
                    ],
                  },
                },
              },
            },
          },
        },
      },
    },
  });
}
